import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Activity,
  Zap,
  Server,
  ArrowUpRight,
  ArrowDownRight,
  Crown,
  Shield,
  Target,
  Brain,
  Sparkles
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PREVIEW MODE - Demo data when backend unavailable
const PREVIEW_MODE = false;

const DEMO_DATA = {
  dashboardData: {
    total_monthly: 405000,
    total_yearly: 4860000,
    avg_growth: 23.2,
    franchises: [
      { name: 'DNCM Immobilier', type: 'immo', monthly_revenue: 125000, yearly_projection: 1500000, growth_rate: 15.5, color: '#DC143C' },
      { name: 'DNCM Voyage', type: 'voyage', monthly_revenue: 85000, yearly_projection: 1020000, growth_rate: 22.3, color: '#00FF9D' },
      { name: 'DNCM Jade', type: 'jade', monthly_revenue: 195000, yearly_projection: 2340000, growth_rate: 31.8, color: '#FFD700' }
    ],
    cascade_data: [
      { month: 'Jan', revenue: 405000, cumulative: 405000 },
      { month: 'Fév', revenue: 420000, cumulative: 825000 },
      { month: 'Mar', revenue: 438000, cumulative: 1263000 },
      { month: 'Avr', revenue: 455000, cumulative: 1718000 },
      { month: 'Mai', revenue: 475000, cumulative: 2193000 },
      { month: 'Jun', revenue: 495000, cumulative: 2688000 },
      { month: 'Jul', revenue: 518000, cumulative: 3206000 },
      { month: 'Aoû', revenue: 542000, cumulative: 3748000 },
      { month: 'Sep', revenue: 568000, cumulative: 4316000 },
      { month: 'Oct', revenue: 595000, cumulative: 4911000 },
      { month: 'Nov', revenue: 624000, cumulative: 5535000 },
      { month: 'Déc', revenue: 655000, cumulative: 6190000 }
    ]
  },
  ecosystemData: {
    total_agents: 17,
    online: 14,
    standby: 2,
    offline: 1,
    avg_cpu: 43.1,
    avg_memory: 53.4,
    vps_status: 'operational',
    vps_ip: '72.60.233.88'
  }
};

// Agent Command Buttons
const AGENT_COMMANDS = [
  { id: 'jade', name: 'JADE', role: 'Strategic Intelligence', icon: Crown, color: '#FFD700', status: 'active' },
  { id: 'lexa', name: 'LEXA', role: 'Communication Handler', icon: Sparkles, color: '#00FF9D', status: 'active' },
  { id: 'atlas', name: 'ATLAS', role: 'Data Processor', icon: Brain, color: '#00BFFF', status: 'active' },
  { id: 'athena', name: 'ATHENA', role: 'Strategic Analyst', icon: Target, color: '#DC143C', status: 'active' },
  { id: 'eva', name: 'EVA', role: 'Executive Assistant', icon: Shield, color: '#9D00FF', status: 'standby' }
];

export const OverviewModule = ({ token }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [ecosystemData, setEcosystemData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [convLogs, setConvLogs] = useState([]);
  const [convStats, setConvStats] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // PREVIEW MODE - Use demo data
      if (PREVIEW_MODE) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setDashboardData(DEMO_DATA.dashboardData);
        setEcosystemData(DEMO_DATA.ecosystemData);
        setLoading(false);
        return;
      }

      try {
        const [financeRes, ecosystemRes] = await Promise.all([
          axios.get(`${API}/finance/dashboard`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/ecosystem/status`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setDashboardData(financeRes.data);
        setEcosystemData(ecosystemRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        // Fallback to demo data on error
        setDashboardData(DEMO_DATA.dashboardData);
        setEcosystemData(DEMO_DATA.ecosystemData);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Fetch conversations
    const fetchLogs = async () => {
      if (!token) return;
      setLogsLoading(true);
      try {
        const [recentRes, statsRes] = await Promise.all([
          axios.get(`${API}/conversations/recent?limit=20`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/conversations/stats`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setConvLogs(recentRes.data.conversations || []);
        setConvStats(statsRes.data.stats || []);
      } catch(e) { console.error('logs fetch error', e); }
      finally { setLogsLoading(false); }
    };
    fetchLogs();
    const logsInterval = setInterval(fetchLogs, 30000);
    return () => clearInterval(logsInterval);
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#DC143C] font-mono text-sm animate-pulse">
          INITIALIZING COMMAND CENTER...
        </div>
      </div>
    );
  }

  const stats = [
    {
      label: 'REVENUS MENSUELS',
      value: `€${(dashboardData?.total_monthly || 0).toLocaleString()}`,
      icon: DollarSign,
      change: '+12.5%',
      positive: true,
      color: '#DC143C'
    },
    {
      label: 'PROJECTION ANNUELLE',
      value: `€${(dashboardData?.total_yearly || 0).toLocaleString()}`,
      icon: TrendingUp,
      change: '+23.1%',
      positive: true,
      color: '#00FF9D'
    },
    {
      label: 'AGENTS ACTIFS',
      value: ecosystemData?.online || 0,
      icon: Users,
      change: `/${ecosystemData?.total_agents || 0}`,
      positive: true,
      color: '#00BFFF'
    },
    {
      label: 'CHARGE SYSTÈME',
      value: `${ecosystemData?.avg_cpu || 0}%`,
      icon: Activity,
      change: 'CPU',
      positive: (ecosystemData?.avg_cpu || 0) < 80,
      color: '#FFD700'
    }
  ];

  const pieData = dashboardData?.franchises?.map(f => ({
    name: f.name.replace('DNCM ', ''),
    value: f.monthly_revenue,
    color: f.color
  })) || [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#0A0A0A] border border-[#DC143C]/50 p-3">
          <p className="font-mono text-xs text-[#888888]">{payload[0].payload.month}</p>
          <p className="font-mono text-sm text-[#EDEDED]">
            €{payload[0].value?.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div data-testid="overview-module" className="space-y-6 animate-fade-in">
      {/* Welcome Header */}
      <div className="grid-card p-6 border-l-2 border-l-[#DC143C]">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold text-[#EDEDED] uppercase tracking-wider mb-1">
              WAR ROOM ACTIVE
            </h2>
            <p className="font-mono text-sm text-[#888888]">
              Tous les systèmes opérationnels • VPS: {ecosystemData?.vps_ip}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#DC143C] animate-pulse" />
            <span className="font-mono text-xs text-[#00FF9D]">LIVE</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div 
            key={stat.label} 
            className="stat-card card-hover corner-accent"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="flex items-start justify-between mb-4">
              <div 
                className="p-2 border"
                style={{ borderColor: `${stat.color}50`, backgroundColor: `${stat.color}10` }}
              >
                <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
              </div>
              <div className={`flex items-center gap-1 font-mono text-xs ${stat.positive ? 'text-[#00FF9D]' : 'text-[#DC143C]'}`}>
                {stat.change}
                {stat.positive ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : (
                  <ArrowDownRight className="w-3 h-3" />
                )}
              </div>
            </div>
            <p className="stat-value">{stat.value}</p>
            <p className="stat-label">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 grid-card">
          <div className="module-header">
            <span className="module-title">CASCADE DE REVENUS</span>
            <span className="module-badge">2024</span>
          </div>
          <div className="p-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dashboardData?.cascade_data || []}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#DC143C" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#DC143C" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="month" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#888888', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#888888', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                  tickFormatter={(value) => `€${(value/1000).toFixed(0)}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="#DC143C" 
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Franchise Distribution */}
        <div className="grid-card">
          <div className="module-header">
            <span className="module-title">FRANCHISES</span>
            <span className="module-badge">{pieData.length}</span>
          </div>
          <div className="p-4 h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="px-4 pb-4 space-y-2">
            {pieData.map((item) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2" style={{ backgroundColor: item.color }} />
                  <span className="font-mono text-xs text-[#888888]">{item.name}</span>
                </div>
                <span className="font-mono text-xs text-[#EDEDED]">
                  €{item.value?.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Agent Command Center */}
      <div className="grid-card">
        <div className="module-header">
          <span className="module-title">AGENT COMMAND CENTER</span>
          <span className="module-badge">5 UNITS</span>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {AGENT_COMMANDS.map((agent) => (
              <button
                key={agent.id}
                data-testid={`agent-${agent.id}-btn`}
                onClick={() => setSelectedAgent(selectedAgent === agent.id ? null : agent.id)}
                className={`p-4 border transition-all duration-300 group ${
                  selectedAgent === agent.id
                    ? 'border-[#DC143C] bg-[#DC143C]/10'
                    : 'border-white/10 hover:border-white/30 bg-[#0A0A0A]'
                }`}
                style={{
                  boxShadow: selectedAgent === agent.id ? `0 0 20px ${agent.color}40` : 'none'
                }}
              >
                <div 
                  className="w-10 h-10 mx-auto mb-3 flex items-center justify-center border"
                  style={{ 
                    borderColor: agent.color,
                    backgroundColor: `${agent.color}15`
                  }}
                >
                  <agent.icon className="w-5 h-5" style={{ color: agent.color }} />
                </div>
                <p className="font-heading text-sm font-bold text-[#EDEDED] text-center uppercase tracking-wider">
                  {agent.name}
                </p>
                <p className="font-mono text-[10px] text-[#888888] text-center mt-1">
                  {agent.role}
                </p>
                <div className="flex items-center justify-center gap-1 mt-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    agent.status === 'active' ? 'bg-[#00FF9D]' : 'bg-[#FFD700]'
                  }`} />
                  <span className="font-mono text-[9px] text-[#888888] uppercase">
                    {agent.status}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Agent Details Panel */}
          {selectedAgent && (
            <div className="mt-4 p-4 bg-[#050505] border border-[#DC143C]/30 animate-fade-in">
              <div className="flex items-center gap-4">
                {(() => {
                  const agent = AGENT_COMMANDS.find(a => a.id === selectedAgent);
                  return (
                    <>
                      <div 
                        className="w-16 h-16 flex items-center justify-center border"
                        style={{ borderColor: agent?.color, backgroundColor: `${agent?.color}10` }}
                      >
                        {agent && <agent.icon className="w-8 h-8" style={{ color: agent.color }} />}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-bold text-[#EDEDED] uppercase">
                          AGENT {agent?.name}
                        </h3>
                        <p className="font-mono text-sm text-[#888888]">{agent?.role}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="font-mono text-xs text-[#00FF9D]">● ONLINE</span>
                          <span className="font-mono text-xs text-[#888888]">CPU: 45%</span>
                          <span className="font-mono text-xs text-[#888888]">MEM: 62%</span>
                        </div>
                      </div>
                      <button className="px-4 py-2 bg-[#DC143C] text-white font-heading text-xs uppercase tracking-widest hover:bg-[#FF2E55] transition-colors">
                        ACTIVATE
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* System Status */}
      <div className="grid-card">
        <div className="module-header">
          <span className="module-title">INFRASTRUCTURE</span>
          <div className="flex items-center gap-2">
            <Server className="w-4 h-4 text-[#00FF9D]" />
            <span className="font-mono text-xs text-[#00FF9D]">OPERATIONAL</span>
          </div>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="font-mono text-xs text-[#888888] mb-1">VPS IP</p>
            <p className="font-mono text-sm text-[#EDEDED]">{ecosystemData?.vps_ip}</p>
          </div>
          <div>
            <p className="font-mono text-xs text-[#888888] mb-1">CPU MOYEN</p>
            <p className="font-mono text-sm text-[#EDEDED]">{ecosystemData?.avg_cpu}%</p>
          </div>
          <div>
            <p className="font-mono text-xs text-[#888888] mb-1">MÉMOIRE</p>
            <p className="font-mono text-sm text-[#EDEDED]">{ecosystemData?.avg_memory}%</p>
          </div>
          <div>
            <p className="font-mono text-xs text-[#888888] mb-1">AGENTS</p>
            <p className="font-mono text-sm text-[#EDEDED]">
              {ecosystemData?.online}/{ecosystemData?.total_agents}
            </p>
          </div>
        </div>
      </div>
      {/* Conversations Live */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stats par agent */}
        <div className="grid-card">
          <div className="module-header">
            <span className="module-title">ACTIVITÉ AGENTS</span>
            <span className="module-badge">{convStats.length} agents</span>
          </div>
          <div className="p-4 space-y-3">
            {convStats.length === 0 ? (
              <p className="font-mono text-xs text-[#444444] text-center py-4">Aucune activité</p>
            ) : convStats.map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00FF9D]" />
                  <span className="font-mono text-xs text-[#EDEDED]">{s.brain}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-[#DC143C]">{s.total} msg</span>
                  <span className="font-mono text-[9px] text-[#444444]">
                    {s.derniere ? new Date(s.derniere).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'}) : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Logs récents */}
        <div className="lg:col-span-2 grid-card">
          <div className="module-header">
            <span className="module-title">LOGS CONVERSATIONS</span>
            <div className="flex items-center gap-2">
              {logsLoading && <span className="font-mono text-[9px] text-[#FFD700] animate-pulse">SYNC...</span>}
              <span className="module-badge">{convLogs.length} entrées</span>
            </div>
          </div>
          <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
            {convLogs.length === 0 ? (
              <p className="font-mono text-xs text-[#444444] text-center py-8">Aucun log — démarrez une conversation</p>
            ) : convLogs.map((log, i) => (
              <div key={log.id} className={`flex gap-3 p-2 border-l-2 ${log.role === 'user' ? 'border-l-[#00BFFF] bg-[#00BFFF]/5' : 'border-l-[#00FF9D] bg-[#00FF9D]/5'}`}>
                <div className="flex flex-col gap-1 min-w-[80px]">
                  <span className="font-mono text-[9px] text-[#DC143C] font-bold">{log.brain}</span>
                  <span className={`font-mono text-[9px] uppercase ${log.role === 'user' ? 'text-[#00BFFF]' : 'text-[#00FF9D]'}`}>{log.role}</span>
                  <span className="font-mono text-[8px] text-[#333333]">
                    {new Date(log.created_at).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit',second:'2-digit'})}
                  </span>
                </div>
                <p className="font-mono text-[10px] text-[#888888] leading-relaxed flex-1 truncate">{log.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default OverviewModule;

